import { useState, useMemo, useEffect } from "react";
import WorldMap from "./WorldMap";
import Arrow from "../../../../../public/images/Arrow";
import MinusIcon from './../../../../../public/images/MinusIcon';
import PlusIcon from './../../../../../public/images/PlusIcon';
import AttentionIcon from "../../../../../public/images/AttentionIcon";
import useStore from "./useStore"; 
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";

const TYPE_LABELS = {
    landline: "Landline",
    mobile: "Mobile",
    national: "National",
    tollfee: "Tollfee",
    random: "Random",
};

const TYPE_ICONS = {
    landline: "/images/Landline.svg",
    mobile: "/images/Mobile.svg",
    national: "/images/National.svg",
    tollfee: "/images/Tollfee.svg",
    random: "/images/Random.svg",
};

function formatPrice(value) {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (Number.isNaN(n)) return "";
    const s = n.toFixed(2);
    return s.endsWith(".00") ? String(Math.trunc(n)) : s;
}

export default function CountryDetails({ country, onBack, onBuy }) {
    const [openTypeKey, setOpenTypeKey] = useState(null);
    const [qtyByType, setQtyByType] = useState({});
    const { addToCart, updateCart, cart } = useStore();

    const existingCountryInCart = cart.find(item => item.country.id === country.id);

    useEffect(() => {
        if (existingCountryInCart) {
            const restoredQty = existingCountryInCart.selectedOptions.reduce((acc, option) => {
                acc[option.typeKey] = option.qty;
                return acc;
            }, {});
            setQtyByType(restoredQty);  
        }
    }, [existingCountryInCart]);

    const optionsEntries = useMemo(
        () => Object.entries(country?.options || {}),
        [country]
    );

    const toggleType = (typeKey) => {
        setOpenTypeKey((prev) => (prev === typeKey ? null : typeKey));
    };

    const inc = (typeKey) => {
        setQtyByType((prev) => ({ ...prev, [typeKey]: (prev[typeKey] ?? 0) + 1 }));
    };

    const dec = (typeKey) => {
        setQtyByType((prev) => {
            const next = Math.max(0, (prev[typeKey] ?? 0) - 1);
            return { ...prev, [typeKey]: next };
        });
    };
    const collectData = () => {
        const selectedOptions = Object.entries(qtyByType).map(([typeKey, qty]) => {
            if (qty > 0) {
                const typeData = country.options[typeKey];
                return {
                    typeKey,
                    qty,
                    price: qty * (typeData?.monthly?.price || 0),  
                };
            }
            return null;
        }).filter(item => item !== null);

        return {
            country,
            selectedOptions,
            quantity: 1
        };
    };

    const totalSelectedOptions = Object.values(qtyByType).reduce((total, qty) => total + qty, 0);

    const handleBuy = () => {
        const data = collectData(); 

        if (existingCountryInCart) {
            updateCart(data); 
        } else {
            addToCart(data);
        }

        console.log("Added to cart:", data); 
        onBuy?.(data); 
        onBack?.();
    };

    return (
        <div className="countriesModal__countriesDetails">
            <div className="countriesModal__countriesDetails__top">
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onBack?.();  
                    }}
                >
                    ←
                </button>
            </div>

            <div className="countriesModal__countriesDetails__info">
                <SimpleBar style={{ maxHeight: "61vh" }}>
                    <WorldMap selectedName={country.name} />

                    <div className="countriesModal__countriesDetails__info__title">
                        <h2>{country.name}</h2>
                        <p>Local calls only</p>
                    </div>

                    <div className="countriesModal__countriesDetails__info__details">
                        <h3>Types of numbers</h3>

                        <div className="countriesModal__countriesDetails__info__details__types">
                            {optionsEntries.map(([typeKey, typeData]) => {
                                const isOpen = openTypeKey === typeKey;
                                const qty = qtyByType[typeKey] ?? 0;

                                return (
                                    <div
                                        key={typeKey}
                                        className={[
                                            "countriesModal__countriesDetails__info__details__types__type",
                                            isOpen ? "isOpen" : "",
                                        ].join(" ")}
                                    >
                                        <div className="countriesModal__countriesDetails__info__details__types__type__top">
                                            <div className="countriesModal__countriesDetails__info__details__types__type__top__left">
                                                <img
                                                    src={TYPE_ICONS[typeKey]}
                                                    alt={typeKey}
                                                />
                                                <div className="countriesModal__countriesDetails__info__details__types__type__top__left__text">
                                                    <h4>{TYPE_LABELS[typeKey] || typeKey}</h4>
                                                    <p>
                                                        Billing step: <span>{typeData.payPeriod}</span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="countriesModal__countriesDetails__info__details__types__type__top__right">
                                                <div className="countriesModal__countriesDetails__info__details__types__type__top__right__counter">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            dec(typeKey);
                                                        }}
                                                        disabled={qty <= 0}
                                                    >
                                                        <MinusIcon />
                                                    </button>

                                                    <span>{qty}</span>

                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            inc(typeKey);
                                                        }}
                                                    >
                                                        <PlusIcon />
                                                    </button>
                                                </div>

                                                <button
                                                    type="button"
                                                    className={[
                                                        "countriesModal__countriesDetails__info__details__types__type__top__right__toggleLinesBtn",
                                                        isOpen ? "isOpen" : "",
                                                    ].join(" ")}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleType(typeKey);
                                                    }}
                                                    aria-expanded={isOpen}
                                                    aria-controls={`lines-${typeKey}`}
                                                >
                                                    <Arrow color="#7B76FF" />
                                                </button>
                                            </div>
                                        </div>

                                        {isOpen && (
                                            <div
                                                id={`lines-${typeKey}`}
                                                className="countriesModal__countriesDetails__info__details__types__type__lines"
                                            >
                                                {typeData.activation?.enabled && (
                                                    <div className="countriesModal__countriesDetails__info__details__types__type__lines__line">
                                                        <h4>
                                                            Activation Fee{" "}
                                                            <span>One-time payment for first purchase</span>
                                                        </h4>
                                                        <h5>
                                                            {formatPrice(typeData.activation.price)}{" "}
                                                            <span>$/Once</span>
                                                        </h5>
                                                    </div>
                                                )}
                                                {typeData.monthly?.enabled && (
                                                    <div className="countriesModal__countriesDetails__info__details__types__type__lines__line">
                                                        <h4>
                                                            Monthly Fee <span>monthly number fee</span>
                                                        </h4>
                                                        <h5>
                                                            {formatPrice(typeData.monthly.price)}{" "}
                                                            <span>$/Month</span>
                                                        </h5>
                                                    </div>
                                                )}
                                                {typeData.outboundRate?.enabled && (
                                                    <div className="countriesModal__countriesDetails__info__details__types__type__lines__line">
                                                        <h4>
                                                            Outbound Rate per minute{" "}
                                                            <span>monthly payment</span>
                                                        </h4>
                                                        <h5>
                                                            {formatPrice(typeData.outboundRate.price)}{" "}
                                                            <span>$/Minute</span>
                                                        </h5>
                                                    </div>
                                                )}
                                                {typeData.inboundRate?.enabled && (
                                                    <div className="countriesModal__countriesDetails__info__details__types__type__lines__line">
                                                        <h4>
                                                            Inbound Rate per minute{" "}
                                                            <span>monthly payment</span>
                                                        </h4>
                                                        <h5>
                                                            {formatPrice(typeData.inboundRate.price)}{" "}
                                                            <span>$/Minute</span>
                                                        </h5>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </SimpleBar>
            </div>

            <button
                className={`countriesModal__countriesDetails__buyNow ${totalSelectedOptions === 0 ? "disabled" : ""}`}
                onClick={handleBuy} 
                disabled={totalSelectedOptions === 0} 
            >
                Buy now
            </button>
            <span className="countriesModal__countriesDetails__attention">
                <AttentionIcon /> Calls are your responsibility
            </span>
        </div>
    );
}